/**
 * User-facing status surface for the LiveKit path.
 *
 * Every failure signal (camera permission, credential refusal, room drop,
 * quality gate, a stroke flood) reaches only the console unless something
 * aggregates it. This model collects the services' events into named fields,
 * renders them to the DOM overlay (visible on the 2D page before entering AR)
 * and to the in-AR HUD (`StatusSpriteSystem`).
 */
import type {
  IAssistSessionService,
  IAssistStrokeService,
  IAssistantRosterService,
  IDetectionService,
  IImageQualifierService,
  ILiveKitRoomService,
  ITrainingStateService,
} from '@questvisionstream/client';

export type StatusField =
  | 'server'
  | 'camera'
  | 'connection'
  | 'assist'
  | 'assistants'
  | 'strokes'
  | 'quality'
  | 'detections'
  | 'training';

/** Display order for {@link StatusModel.lines}. */
const FIELD_ORDER: readonly StatusField[] = [
  'server',
  'camera',
  'connection',
  'assist',
  'assistants',
  'strokes',
  'quality',
  'detections',
  'training',
];

export class StatusModel {
  private readonly fields = new Map<StatusField, string>();
  private readonly listeners = new Set<() => void>();

  set(field: StatusField, value: string): void {
    if (this.fields.get(field) === value) return;
    this.fields.set(field, value);
    for (const listener of this.listeners) listener();
  }

  get(field: StatusField): string | undefined {
    return this.fields.get(field);
  }

  /** Stable-ordered `field: value` lines for display. */
  lines(): string[] {
    return FIELD_ORDER.filter((f) => this.fields.has(f)).map((f) => `${f}: ${this.fields.get(f)}`);
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  snapshot(): Partial<Record<StatusField, string>> {
    return Object.fromEntries(this.fields) as Partial<Record<StatusField, string>>;
  }

  /** True when the room is up - drives the HUD dot. */
  isConnected(): boolean {
    return this.fields.get('connection') === 'connected';
  }

  /** Compact lines for the in-AR panel: the `server` value shortened to its host. */
  panelLines(): string[] {
    return this.lines().map((line) => {
      if (!line.startsWith('server: ')) return line;
      const value = line.slice('server: '.length);
      try {
        return `server: ${new URL(value).host || value}`;
      } catch {
        return line;
      }
    });
  }

  reset(): void {
    this.fields.clear();
    for (const listener of this.listeners) listener();
  }
}

/** The app-wide status instance systems report into. */
export const status = new StatusModel();

/** Render the model into a DOM container, one line per field, kept live. */
export function bindStatusDom(model: StatusModel, container: HTMLElement): () => void {
  const render = (): void => {
    container.textContent = model.lines().join('\n');
  };
  render();
  return model.onChange(render);
}

export interface StatusServices {
  readonly room?: ILiveKitRoomService;
  readonly detection?: IDetectionService;
  readonly qualifier?: IImageQualifierService;
  readonly assist?: IAssistSessionService;
  readonly roster?: IAssistantRosterService;
  readonly strokes?: IAssistStrokeService;
  readonly training?: ITrainingStateService;
}

/** Subscribe the model to the services' events. Returns a single unsubscribe for everything. */
export function wireStatusServices(model: StatusModel, services: StatusServices): () => void {
  const unsubs: Array<() => void> = [];
  const { room, detection, qualifier, assist, roster, strokes, training } = services;

  if (room) {
    model.set('server', room.configEndpoint || '(same origin)');
    model.set('connection', room.connectionState);
    unsubs.push(room.on('stateChange', (state) => model.set('connection', state)));
    unsubs.push(room.on('discoveryFailed', (reason) => model.set('connection', `failed - ${reason}`)));
    unsubs.push(room.on('remoteConfig', () => model.set('server', room.configEndpoint)));
  }
  if (assist) {
    const render = (): void => model.set('assist', `${assist.state}${assist.microphoneLive ? ' · mic live' : ''}`);
    render();
    unsubs.push(assist.on('stateChange', render));
    unsubs.push(assist.on('microphoneLiveChanged', render));
    unsubs.push(assist.on('qualityChanged', ({ line }) => model.set('assistants', `${roster?.count ?? 0} · ${line}`)));
  }
  if (roster) {
    const render = (): void =>
      model.set('assistants', `${roster.count}${roster.assistantsMuted ? ' · muted by wearer' : ''}`);
    render();
    unsubs.push(roster.on('assistantJoined', render));
    unsubs.push(roster.on('assistantLeft', render));
    unsubs.push(roster.on('allMutedChanged', render));
  }
  if (strokes) {
    unsubs.push(
      strokes.on('strokeReceived', () =>
        model.set('strokes', `${strokes.receivedCount} ok · ${strokes.droppedCount} dropped · ${strokes.lateCount} late`),
      ),
    );
  }
  if (qualifier) {
    unsubs.push(
      qualifier.on('quality', (report) => {
        const detail = report.metrics
          .map((m) => `${m.name} ${Math.round(m.value)}${m.detail ? ` ${m.detail}` : ''}`)
          .join(', ');
        model.set('quality', `${report.ok ? 'ok' : 'paused'}${detail ? ` - ${detail}` : ''}`);
      }),
    );
  }
  if (detection) {
    unsubs.push(
      detection.on('detections', (payload) =>
        model.set('detections', `${payload.detections.length} @ frame ${payload.frame}`),
      ),
    );
  }
  if (training) {
    const render = (): void => {
      const scenario = training.scenario?.name ?? '(none)';
      const step = training.currentStep ? `step ${training.currentStepIndex + 1}: ${training.currentStep.title}` : training.status;
      model.set('training', `${scenario} · ${step}`);
    };
    render();
    unsubs.push(training.on('scenarioLoaded', render));
    unsubs.push(training.on('stepActivated', render));
    unsubs.push(training.on('scenarioCompleted', render));
  }

  return () => {
    for (const unsub of unsubs.splice(0)) unsub();
  };
}
